import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

// Import the unsafe versions so we do not trigger initialization
import { useResourceLibraryItemsUnsafe } from './ResourceLibraryItemsProvider';
import { useProductGroupsUnsafe } from './ProductGroupsProvider';
import { useProductApplicationsUnsafe } from './ProductApplicationsProvider';
import { useResourceTypesUnsafe } from './ResourceTypesProvider';
import { useBrandsUnsafe } from './BrandsProvider';
import { useNavigationNodesUnsafe } from '../../siteNavigation/contexts/NavigationNodesProvider';

import {
    filterDescriptors,
    KeyFilters,
    FilterName,
    Filters,
    emptyFilterMatchesOnlyEmptyKeys,
} from '../types/filters';
import { SortOrder } from '../types/sorting';
import {
    filterItemsWithRelevance,
    toggleValueInArray,
    convertFilterKeysToFilters,
} from './filters';
import { ResourceLibraryItem } from '../types/resourceLibraryItems';
import { getCounts, Counts } from './counts';

interface PrefilteredResourceLibraryItemsContextValue {
    // The final filtered items (after applying initial and then context filters)
    filteredItems: ResourceLibraryItem[];
    paginatedItems: ResourceLibraryItem[];
    // Items after applying only the initial filters
    prefilteredItems: ResourceLibraryItem[];
    totalPages: number;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    itemsPerPage: number;
    sortOrder: SortOrder;
    setSortOrder: (sortOrder: SortOrder) => void;
    loading: boolean;
    error: any;
    initialize: () => void;
    filters: Filters;
    setFilter: <K extends FilterName>(filterName: K, value: Filters[K]) => void;
    toggleFilter: (filterName: FilterName, value: string) => void;
    resetFilters: () => void;
    /** Counts reflecting initial filters only */
    counts: Counts;
}

const PrefilteredResourceLibraryItemsContext = createContext<
    PrefilteredResourceLibraryItemsContextValue | undefined
>(undefined);

interface PrefilteredResourceLibraryItemsProviderProps {
    children: ReactNode;
    initialFilters: KeyFilters;
    itemsPerPage?: number;
    sortOrder?: SortOrder;
}

const PrefilteredResourceLibraryItemsProvider = ({
    children,
    initialFilters,
    itemsPerPage = 10,
    sortOrder: initialSortOrder = 'latest',
}: PrefilteredResourceLibraryItemsProviderProps) => {
    const [initialized, setInitialized] = useState(false);
    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
            initializeDependentProviders();
        }
    };

    const {
        items,
        initialize: initializeResourceLibraryItems,
        loading: isResourceLibraryLoading,
        error: resourceLibraryError,
    } = useResourceLibraryItemsUnsafe();

    const {
        productGroupById,
        productGroupByKey,
        initialize: initializeProductGroups,
    } = useProductGroupsUnsafe();
    const {
        productApplicationById,
        productApplicationByKey,
        initialize: initializeProductApplications,
    } = useProductApplicationsUnsafe();
    const {
        resourceTypeById,
        resourceTypeByKey,
        initialize: initializeResourceTypes,
    } = useResourceTypesUnsafe();
    const { brandByKey, initialize: initializeBrands } = useBrandsUnsafe();
    const {
        navigationNodeById,
        navigationNodeByPath,
        initialize: initializeNavigationNodes,
    } = useNavigationNodesUnsafe();

    const initializeDependentProviders = () => {
        initializeResourceLibraryItems();
        initializeProductGroups();
        initializeProductApplications();
        initializeResourceTypes();
        initializeBrands();
        initializeNavigationNodes();
    };

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder);
    const [filters, setFilters] = useState<Filters>({
        brandId: null,
        productGroupIds: [],
        productApplicationIds: [],
        resourceTypeIds: [],
        navigationScopeIds: [],
        exclusiveNavigationScopeIds: [],
        assetPaths: [],
        searchTerm: null,
    });

    const setFilter = <K extends FilterName>(filterName: K, value: Filters[K]) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [filterName]: value,
        }));
    };

    const toggleFilter = <K extends FilterName>(filterName: K, value: string) => {
        setFilters((prevFilters) => {
            const currentFilterValue = prevFilters[filterName];

            if (filterDescriptors[filterName].isArray) {
                const currentArray = currentFilterValue as string[];
                return {
                    ...prevFilters,
                    [filterName]: toggleValueInArray(currentArray, value),
                };
            } else {
                const currentValue = currentFilterValue as string | null;
                return {
                    ...prevFilters,
                    [filterName]: currentValue === value ? null : value,
                };
            }
        });
    };

    // Compute the resolved filters from the initialFilters prop.
    const resolvedFilters = useMemo(
        (): Filters =>
            convertFilterKeysToFilters(initialFilters, {
                brandByKey,
                productGroupByKey,
                productApplicationByKey,
                resourceTypeByKey,
                navigationNodeByPath,
            }),
        [
            initialFilters,
            brandByKey,
            productGroupByKey,
            productApplicationByKey,
            resourceTypeByKey,
            navigationNodeByPath,
        ]
    );

    // Debug log initialFilters
    useEffect(() => {
        console.debug('PrefilteredResourceLibraryItemsProvider initialFilters:', initialFilters);
    }, [initialFilters]);

    // Build overrides immutably for selected keys
    function pickKeys<T extends object>(src: T, keys: readonly (keyof T)[]) {
        return Object.fromEntries(keys.map((k) => [k, src[k]])) as Partial<T>;
    }

    const resetFilters = () => {
        setFilters({
            brandId: null,
            productGroupIds: [],
            productApplicationIds: [],
            resourceTypeIds: [],
            navigationScopeIds: [],
            assetPaths: [],
            searchTerm: null,
            ...pickKeys(resolvedFilters, emptyFilterMatchesOnlyEmptyKeys),
        });
    };

    useEffect(() => {
        setFilters((prev) => ({
            ...prev,
            ...pickKeys(resolvedFilters, emptyFilterMatchesOnlyEmptyKeys),
        }));
    }, [resolvedFilters]);

    // First, prefilter the items using the resolved (initial) filters.

    const prefilteredItemsWithRelevance = useMemo(() => {
        return filterItemsWithRelevance({
            items,
            filters: resolvedFilters,
            sortOrder,
            filterResolvers: {
                productGroupById,
                productApplicationById,
                resourceTypeById,
                navigationNodeById,
            },
            computeRelevance: false,
        });
    }, [
        items,
        resolvedFilters,
        sortOrder,
        productGroupById,
        productApplicationById,
        resourceTypeById,
        navigationNodeById,
    ]);

    const prefilteredItems = useMemo(
        () => prefilteredItemsWithRelevance.map((result) => result.item),
        [prefilteredItemsWithRelevance]
    );
    // Calculate counts based on initial (prefiltered) items
    const counts = useMemo<Counts>(
        () =>
            getCounts(prefilteredItems, productGroupById, productApplicationById, resourceTypeById),
        [prefilteredItems, productGroupById, productApplicationById, resourceTypeById]
    );

    // Then, if any filters have been set via context, further filter the prefiltered items.
    const finalFilteredItemsWithRelevance = useMemo(() => {
        // Check if any context filters are provided (for scalar values, a non-null value indicates a filter)
        const hasAdditionalFilters =
            filters.brandId !== null ||
            (filters.productGroupIds && filters.productGroupIds.length > 0) ||
            (filters.productApplicationIds && filters.productApplicationIds.length > 0) ||
            (filters.resourceTypeIds && filters.resourceTypeIds.length > 0) ||
            (filters.assetPaths && filters.assetPaths.length > 0) ||
            filters.searchTerm !== null;

        if (!hasAdditionalFilters) {
            return prefilteredItemsWithRelevance;
        }

        return filterItemsWithRelevance({
            items: prefilteredItems,
            filters: filters,
            sortOrder,
            filterResolvers: {
                productGroupById,
                productApplicationById,
                resourceTypeById,
                navigationNodeById,
            },
            computeRelevance: false,
        });
    }, [
        prefilteredItems,
        prefilteredItemsWithRelevance,
        filters,
        sortOrder,
        productGroupById,
        productApplicationById,
        resourceTypeById,
        navigationNodeById,
    ]);

    const filteredItems = useMemo(() => {
        return finalFilteredItemsWithRelevance.map((result) => result.item);
    }, [finalFilteredItemsWithRelevance]);

    const totalPages = useMemo(() => {
        return Math.ceil(filteredItems.length / itemsPerPage);
    }, [filteredItems, itemsPerPage]);

    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredItems.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredItems, currentPage, itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    return (
        <PrefilteredResourceLibraryItemsContext.Provider
            value={{
                filteredItems,
                paginatedItems,
                prefilteredItems,
                totalPages,
                currentPage,
                setCurrentPage,
                itemsPerPage,
                sortOrder,
                setSortOrder,
                loading: isResourceLibraryLoading,
                error: resourceLibraryError,
                initialize,
                filters,
                setFilter,
                toggleFilter,
                resetFilters,
                counts,
            }}
        >
            {children}
        </PrefilteredResourceLibraryItemsContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function usePrefilteredResourceLibraryItemsUnsafe() {
    const context = useContext(PrefilteredResourceLibraryItemsContext);
    if (!context) {
        throw new Error(
            'usePrefilteredResourceLibraryItems must be used within a PrefilteredResourceLibraryItemsProvider'
        );
    }
    return context;
}

// Safe hook: triggers lazy initialization when a consumer uses it.
function usePrefilteredResourceLibraryItems() {
    const context = usePrefilteredResourceLibraryItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export {
    PrefilteredResourceLibraryItemsProvider,
    usePrefilteredResourceLibraryItems,
    usePrefilteredResourceLibraryItemsUnsafe,
};
export type { PrefilteredResourceLibraryItemsContextValue };
